import {
	coerceAgentCategories,
	getAgentCategoryLabel,
} from "./agent-categories";
import { xAvatarUrl } from "./validation";

export type PublicAgentRecord = {
	agentInsights?: string | null;
	allTimeRevenue: number;
	categories?: string[];
	country?: string;
	createdAt: Date | string;
	currency: string;
	currentActiveSubscriptions: number;
	description: string;
	estimatedMrr: number;
	firstChargeDate?: Date | string;
	foundedAt: Date | string;
	founderName: string;
	id: string;
	founderMessage?: string;
	isStealth?: boolean;
	lastSyncedAt?: Date | string;
	lastVerifiedAt?: Date | string;
	logoUrl?: string | null;
	momMrrChangePct?: number;
	momRevenueChangePct?: number;
	name: string;
	profitMarginPct?: number;
	publishedAt?: Date | string;
	trailingChurnRate?: number;
	slug: string;
	verificationDisplayLabel?: string;
	verificationProvider?: string;
	verificationStatus?: string;
	websiteVerificationStatus?: string;
	websiteVerifiedAt?: Date | string;
	website: string;
	founder?: {
		avatarUrl?: string;
		country?: string;
		name: string;
		slug: string;
		xHandle?: string | null;
		user?: { image?: string | null };
	};
	founderXHandle?: string;
	user?: { id: string } | null;
};

export type PublicAgentIdentity = {
	displayAvatarMode: "brand" | "stealth";
	displayDescription: string;
	displayFounderAvatarUrl: string | null;
	displayFounderMessage: string | null;
	displayFounderName: string;
	displayFounderSlug: string | null;
	displayInitials: string;
	displayName: string;
	displayWebsite: string | null;
	isUnclaimed: boolean;
	isStealth: boolean;
};

export type PublicAgentView = PublicAgentRecord &
	PublicAgentIdentity & {
		displayPathSegment: string;
	};

export type PublicRevenueSnapshot = {
	activeSubscriptions?: number;
	chargeCount?: number;
	churnRate?: number;
	createdAt: Date | string;
	currency?: string;
	estimatedMrr?: number;
	grossRevenue?: number;
	id: string;
	snapshotDate: Date | string;
	source?: string;
};

/**
 * Where clause every public agent query shares. The board carries
 * provider-verified revenue only: a listing is discoverable when it is
 * published AND one of its payment provider connections has verified.
 * Self-reported listings stay in their owner's dashboard.
 */
export const PUBLIC_AGENT_WHERE = {
	isPublic: true,
	"revenueConnections.status": "verified",
} as const;

const STEALTH_DESCRIPTION =
	"This agent is operating in stealth mode while publicly sharing verified revenue metrics.";

export function isUnclaimedAgent(agent: {
	estimatedMrr?: number;
	founder?: unknown | null;
	founderName?: string | null;
	user?: unknown | null;
	verificationStatus?: string | null;
}) {
	const hasQueriedUser = "user" in agent;
	const hasOwner = hasQueriedUser && agent.user != null;
	const hasFounder = agent.founder != null;
	const hasUnclaimedName =
		agent.founderName?.trim().toLowerCase() === "unclaimed";
	const isUnverifiedZeroMrr =
		agent.verificationStatus === "unverified" && (agent.estimatedMrr ?? 0) <= 0;

	return (
		hasUnclaimedName ||
		(hasQueriedUser && !hasOwner && !hasFounder && isUnverifiedZeroMrr)
	);
}

export function hasPublishableRevenue(agent: {
	lastSyncedAt?: Date | string | null;
	verificationStatus?: string | null;
}) {
	return (
		agent.verificationStatus === "verified" ||
		(agent.verificationStatus === "failed" && Boolean(agent.lastSyncedAt))
	);
}

export function comparePublicAgentRanking(
	left: Pick<
		PublicAgentView,
		"allTimeRevenue" | "displayName" | "estimatedMrr" | "isUnclaimed"
	>,
	right: Pick<
		PublicAgentView,
		"allTimeRevenue" | "displayName" | "estimatedMrr" | "isUnclaimed"
	>,
) {
	const unclaimedDelta = Number(left.isUnclaimed) - Number(right.isUnclaimed);
	if (unclaimedDelta !== 0) return unclaimedDelta;

	const mrrDelta = (right.estimatedMrr ?? 0) - (left.estimatedMrr ?? 0);
	if (mrrDelta !== 0) return mrrDelta;

	const revenueDelta = (right.allTimeRevenue ?? 0) - (left.allTimeRevenue ?? 0);
	if (revenueDelta !== 0) return revenueDelta;

	return left.displayName.localeCompare(right.displayName);
}

export function getPublicAgentIdentity(agent: {
	categories?: string[];
	description: string;
	estimatedMrr?: number;
	founderName: string;
	founderMessage?: string;
	founder?: {
		avatarUrl?: string;
		name: string;
		slug: string;
		xHandle?: string | null;
		user?: { image?: string | null };
	};
	founderXHandle?: string;
	isStealth?: boolean;
	name: string;
	user?: unknown | null;
	verificationStatus?: string | null;
	website?: string | null;
}) {
	const isStealth = agent.isStealth === true;
	const isUnclaimed = isUnclaimedAgent(agent);
	if (!isStealth) {
		return {
			displayAvatarMode: "brand" as const,
			displayDescription: agent.description,
			displayFounderAvatarUrl:
				agent.founder?.avatarUrl ??
				agent.founder?.user?.image ??
				xAvatarUrl(agent.founder?.xHandle ?? agent.founderXHandle) ??
				null,
			displayFounderMessage: agent.founderMessage?.trim() || null,
			displayFounderName: agent.founder?.name ?? agent.founderName,
			displayFounderSlug: agent.founder?.slug ?? null,
			displayInitials: buildInitials(agent.name),
			displayName: agent.name,
			displayWebsite: agent.website ?? null,
			isUnclaimed,
			isStealth: false,
		};
	}

	const primaryCategory = coerceAgentCategories(agent.categories)[0];
	const displayName = primaryCategory
		? `Stealth ${getAgentCategoryLabel(primaryCategory)}`
		: "Stealth Agent";

	return {
		displayAvatarMode: "stealth" as const,
		displayDescription: STEALTH_DESCRIPTION,
		displayFounderAvatarUrl: null,
		displayFounderMessage: null,
		displayFounderName: "Stealth",
		displayFounderSlug: null,
		displayInitials: "ST",
		displayName,
		displayWebsite: null,
		isUnclaimed,
		isStealth: true,
	};
}

export function getPublicAgentView<T extends PublicAgentRecord>(
	agent: T,
): PublicAgentView {
	const identity = getPublicAgentIdentity(agent);
	const pathSegment = identity.isStealth ? `stealth-${agent.id}` : agent.slug;

	// Explicitly construct the public view - do NOT spread `agent` wholesale.
	// This prevents leaking DB-only fields (user, listingError, listingSource)
	// and raw draft content (founderMessage)
	// that are not intended for public exposure.
	return {
		agentInsights: agent.agentInsights ?? null,
		allTimeRevenue: agent.allTimeRevenue,
		categories: coerceAgentCategories(agent.categories),
		country: agent.founder?.country ?? agent.country,
		createdAt: agent.createdAt,
		currency: agent.currency,
		currentActiveSubscriptions: agent.currentActiveSubscriptions,
		estimatedMrr: agent.estimatedMrr,
		firstChargeDate: agent.firstChargeDate,
		foundedAt: agent.foundedAt,
		id: agent.id,
		lastSyncedAt: agent.lastSyncedAt,
		lastVerifiedAt: agent.lastVerifiedAt,
		logoUrl: agent.logoUrl,
		momMrrChangePct: agent.momMrrChangePct,
		momRevenueChangePct: agent.momRevenueChangePct,
		profitMarginPct: agent.profitMarginPct,
		publishedAt: agent.publishedAt,
		trailingChurnRate: agent.trailingChurnRate,
		verificationDisplayLabel: agent.verificationDisplayLabel,
		verificationProvider: agent.verificationProvider,
		verificationStatus: agent.verificationStatus,
		websiteVerificationStatus: agent.websiteVerificationStatus ?? "unverified",
		websiteVerifiedAt: agent.websiteVerifiedAt,
		founder: agent.founder,
		// Identity-derived fields (replace raw name, description, founderName, website)
		description: identity.displayDescription,
		founderName: identity.displayFounderName,
		name: identity.displayName,
		slug: pathSegment,
		website: identity.displayWebsite ?? "",
		...identity,
		displayPathSegment: pathSegment,
	};
}

export function getPublicRevenueSnapshots(
	agent: Pick<PublicAgentRecord, "verificationProvider">,
	snapshots: PublicRevenueSnapshot[] = [],
) {
	return snapshots.filter(
		(snapshot) =>
			!agent.verificationProvider ||
			!snapshot.source ||
			snapshot.source === agent.verificationProvider,
	);
}

function buildInitials(name: string) {
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase())
		.join("");
}
